// src/components/OrderConfirmationPage.jsx
//
// Handles two navigation sources:
//
//  A) REST checkout flow (from CheckoutPage.js):
//     state = { orderResponse, shippingInfo, billingAddress, paymentMethod,
//               cardLastFour, subtotal, shipping, tax, discount, total, orderedItems }
//
//  B) Agent / chat flow (from ChatBot.js confirm_purchase):
//     state = { isAgentFlow: true, message, totalAmount, purchasedItems,
//               articleIds, orderedItems, conversationId }
//
import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { trackCheckoutStep } from "../services/uiAgentSync";
import {
  normalizeAgentLines,
  normalizeAgentTotals,
  buildBillRowsFromTotals,
  DEFAULT_AGENT_TAX_PERCENT,
} from "../utils/agentBillingPayload";
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Stack,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Stepper,
  Step,
  StepLabel,
  styled,
} from "@mui/material";
import {
  CheckCircle,
  Download,
  Email,
  LocationOn,
  CreditCard,
} from "@mui/icons-material";

// ── Stepper icon ─────────────────────────────────────────────────────────

const QontoStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  color: theme.palette.mode === "dark" ? theme.palette.grey[700] : "#eaeaf0",
  display: "flex",
  height: 22,
  alignItems: "center",
  ...(ownerState.active && { color: "#784af4" }),
  "& .QontoStepIcon-completedIcon": { color: "#34A853", zIndex: 1, fontSize: 18 },
  "& .QontoStepIcon-circle": { width: 8, height: 8, borderRadius: "50%", backgroundColor: "currentColor" },
}));

function QontoStepIcon({ active, completed, className }) {
  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? <CheckCircle className="QontoStepIcon-completedIcon" /> : <div className="QontoStepIcon-circle" />}
    </QontoStepIconRoot>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function fmt(price) {
  const n = typeof price === "string" ? parseFloat(price) : price;
  return isNaN(n) || n == null ? "0.00" : n.toFixed(2);
}

function roundMoney(n) {
  return Math.round(parseFloat(String(n ?? 0)) * 100) / 100;
}

function fmtAddress(addr = {}) {
  return [
    `${addr.firstName || addr.first_name || ""} ${addr.lastName || addr.last_name || ""}`.trim(),
    addr.streetAddress || addr.street_address,
    addr.city,
    addr.state,
    addr.zipCode || addr.zip_code,
    addr.country,
  ].filter(Boolean).join(", ") || "—";
}

function makeOrderNumber(orderResponse = {}) {
  return (
    orderResponse.order_id ||
    orderResponse.order_number ||
    orderResponse.id ||
    `ORD-${Math.random().toString(36).substring(2, 10).toUpperCase()}`
  );
}

// ── Main component ────────────────────────────────────────────────────────

export default function OrderConfirmationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearCart } = useCart();

  const state = location.state;

  // Guard: no state at all → someone visited the URL directly
  useEffect(() => {
    if (!state) navigate("/", { replace: true });
  }, [state, navigate]);

  // Clear cart once after a successful order confirmation load (Cart API).
  useEffect(() => {
    if (!state) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await clearCart();
      } catch (err) {
        if (!cancelled) {
          console.warn("[OrderConfirmation] clearCart failed:", err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state, clearCart]);

  // UI → Agent sync: order successfully placed (deduped in the tracker)
  useEffect(() => {
    if (state) {
      trackCheckoutStep("order_placed");
    }
  }, [state]);

  if (!state) return null;

  // Agent / chat purchase — detect via flag or payload shape from OrbieeFlow
  const isAgentFlow = Boolean(
    state.isAgentFlow ||
      state.fromChat ||
      (Array.isArray(state.purchasedItems) && state.purchasedItems.length > 0) ||
      state.totalAmount != null
  );

  // ── Data extraction — works for both state shapes ─────────────────────

  const orderDate = new Date();
  const deliveryDate = new Date(orderDate);
  deliveryDate.setDate(deliveryDate.getDate() + 5);

  let orderNumber, shippingDisplay, paymentDisplay, subtotal, shipping, tax, discount, total, items;

  if (isAgentFlow && !state.orderResponse) {
    // Agent / chat-driven purchase — bill from agent lines + totals
    const agentLines = normalizeAgentLines(
      Array.isArray(state.lines) && state.lines.length
        ? state.lines
        : state.summary?.lines
    );
    const agentTotals = normalizeAgentTotals(
      state.totals ?? state.summary?.totals
    );

    const orderIds = state.summary?.order_ids;
    orderNumber =
      Array.isArray(orderIds) && orderIds.length
        ? String(orderIds[0])
        : makeOrderNumber({});

    shippingDisplay = "Confirmed via Orbiee Assistant";
    paymentDisplay = "Processed by Orbiee Assistant";

    if (agentTotals) {
      console.log("agentTotals", agentTotals);
      subtotal = agentTotals.subtotal;
      shipping = agentTotals.shipping;
      tax = agentTotals.tax;
      discount = agentTotals.discountTotal;
      total = agentTotals.total;
    } else {
      const agentTotal =
        Number(state.totalAmount ?? state.summary?.total ?? 0) || 0;
      const lineSubtotal = agentLines.reduce(
        (sum, line) => sum + (line.lineTotal ?? (line.price ?? 0) * (line.quantity ?? 1)),
        0
      );
      subtotal = lineSubtotal > 0 ? lineSubtotal : agentTotal;
      shipping = 0;
      discount = 0;
      total = agentTotal || subtotal;
      const derived = normalizeAgentTotals({ subtotal, total });
      tax = derived?.tax ?? roundMoney(subtotal * DEFAULT_AGENT_TAX_PERCENT / 100);
      if (derived) {
        total = derived.total;
      } else if (total <= subtotal) {
        total = roundMoney(subtotal + tax);
      }
    }

    if (agentLines.length > 0) {
      items = agentLines;
    } else {
      const rawItems =
        (Array.isArray(state.purchasedItems) && state.purchasedItems.length
          ? state.purchasedItems
          : null) ||
        (Array.isArray(state.orderedItems) && state.orderedItems.length
          ? state.orderedItems
          : null) ||
        (Array.isArray(state.summary?.items) ? state.summary.items : []) ||
        [];
      items = rawItems.map((item) => ({
        ...item,
        id: item.id ?? item.product_id ?? item.article_id,
        title: item.title || item.name || item.product_title || "Item",
        quantity: item.quantity ?? 1,
        price: parseFloat(String(item.price ?? 0)) || 0,
        image: item.image || item.image_uri || item.uri || "",
        image_uri: item.image_uri || item.image || item.uri || "",
        size: item.size ?? null,
      }));
    }
  } else {
    // Standard REST checkout (manual) — leave details as provided by CheckoutPage
    orderNumber = makeOrderNumber(state.orderResponse || {});
    shippingDisplay = fmtAddress(state.shippingInfo || {});
    const paymentMethod = state.paymentMethod || "card";
    paymentDisplay =
      paymentMethod === "paypal"
        ? "PayPal"
        : paymentMethod === "applepay"
        ? "Apple Pay"
        : `Credit Card${state.cardLastFour ? ` ending in ${state.cardLastFour}` : ""}`;
    subtotal = state.subtotal ?? 0;
    shipping = 0;
    discount = state.discount ?? 0;
    const taxableSubtotal = Math.max(0, subtotal - discount);
    tax =
      state.tax ??
      roundMoney(taxableSubtotal * DEFAULT_AGENT_TAX_PERCENT / 100);
    total =
      state.total ?? roundMoney(taxableSubtotal + tax);
    items = state.orderedItems || [];
  }

  const customerEmail = state.shippingInfo?.email || null;

  const agentTotalsForRows = isAgentFlow && !state.orderResponse
    ? normalizeAgentTotals(
        state.totals ??
          state.summary?.totals ?? {
            subtotal,
            total,
          }
      )
    : null;

  const priceRows = agentTotalsForRows
  ? buildBillRowsFromTotals(
    { ...agentTotalsForRows, subtotal: agentTotalsForRows.originalSubtotal },
    fmt
  )
    : [
        { label: "Subtotal", value: `$${fmt(subtotal)}` },
        {
          label: `Tax (${DEFAULT_AGENT_TAX_PERCENT}%)`,
          value: `$${fmt(tax)}`,
        },
        ...(discount > 0
          ? [{ label: "Discount", value: `-$${fmt(discount)}`, green: true }]
          : []),
        { label: "Total", value: `$${fmt(total)}`, bold: true },
      ];

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Avatar sx={{ bgcolor: "success.main", width: 64, height: 64, mx: "auto", mb: 2 }}>
          <CheckCircle fontSize="large" />
        </Avatar>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Order Confirmed!
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {isAgentFlow
            ? state.message || "Your purchase has been completed by Orbiee Assistant."
            : "Thank you for your purchase. Your order has been received."}
        </Typography>
        {customerEmail && (
          <Typography variant="body2" color="text.secondary">
            A confirmation will be sent to <strong>{customerEmail}</strong>.
          </Typography>
        )}
      </Box>

      {/* Order banner */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: "primary.main", color: "white", borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ opacity: 0.8 }} display="block">Order Number</Typography>
            <Typography variant="h6" fontWeight="bold">{orderNumber}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Placed on {orderDate.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", sm: "flex-end" }}>
              <Button
                startIcon={<Download />}
                variant="outlined"
                size="small"
                sx={{ color: "white", borderColor: "rgba(255,255,255,0.5)", "&:hover": { bgcolor: "rgba(255,255,255,0.1)" } }}
              >
                Invoice
              </Button>
              {customerEmail && (
                <Button
                  startIcon={<Email />}
                  variant="contained"
                  size="small"
                  sx={{ bgcolor: "white", color: "primary.main", "&:hover": { bgcolor: "rgba(255,255,255,0.9)" } }}
                  onClick={() => (window.location.href = `mailto:${customerEmail}`)}
                >
                  Email
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Timeline */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>Order Timeline</Typography>
        <Stepper orientation="vertical" sx={{ pl: 2 }}>
          {[
            {
              label: "Order Confirmed",
              caption: orderDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
              body: "Your order has been received and confirmed.",
              active: true,
              completed: true,
            },
            {
              label: "Processing",
              caption: "In Progress",
              body: "We're preparing your items for shipment.",
              active: true,
              completed: false,
            },
            {
              label: "Shipped",
              caption: "Pending",
              body: "You'll receive tracking information soon.",
              active: false,
              completed: false,
            },
            {
              label: "Delivered",
              caption: `Est. ${deliveryDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`,
              body: "Estimated delivery date.",
              active: false,
              completed: false,
            },
          ].map(({ label, caption, body, active, completed }) => (
            <Step key={label} active={active} completed={completed}>
              <StepLabel StepIconComponent={QontoStepIcon}>
                <Typography variant="subtitle1" fontWeight="bold">{label}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">{caption}</Typography>
                <Typography variant="body2" color="text.secondary">{body}</Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Shipping & Payment */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, height: "100%", borderRadius: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <LocationOn color="primary" />
              <Typography variant="h6">Shipping Address</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">{shippingDisplay}</Typography>
            {!isAgentFlow && state.shippingInfo?.phone && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {state.shippingInfo.phone}
              </Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, height: "100%", borderRadius: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <CreditCard color="success" />
              <Typography variant="h6">Payment</Typography>
            </Stack>
            <Typography variant="body2" fontWeight="medium">{paymentDisplay}</Typography>
            <Typography variant="body2" color="success.main" sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
              <CheckCircle fontSize="small" /> Payment Successful
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Order items */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Order Items{items.length > 0 ? ` (${items.length})` : ""}
        </Typography>

        {items.length > 0 ? (
          <List disablePadding>
            {items.map((item, index) => {
              const img = Array.isArray(item.image) ? item.image[0] : (item.image || item.image_uri || "");
              const title = item.title || item.name || item.product_title || item.article_id || "Item";
              const qty = item.quantity ?? 1;
              const isAgentLine = item.priceMatched != null;
              const lineTotal = isAgentLine
                ? (item.lineTotal ?? (parseFloat(String(item.price ?? 0)) || 0) * qty)
                : (parseFloat(String(item.price ?? 0)) || 0) * qty;
              return (
                <React.Fragment key={item.cartLineUid || item.id || item.product_id || index}>
                  <ListItem sx={{ py: 2, px: 0, display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <ListItemAvatar>
                      <Avatar variant="rounded" sx={{ width: 60, height: 60, bgcolor: "grey.200", border: "1px solid #ddd" }}>
                        {img ? (
                          <img src={img} alt={title} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} />
                        ) : (
                          <Box sx={{ color: "grey.400", fontSize: 24 }}>?</Box>
                        )}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Typography variant="body1" fontWeight="medium">{title}</Typography>}
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {item.size ? `Size: ${item.size} · ` : ""}Qty: {qty}
                        </Typography>
                      }
                      sx={{ flex: 1 }}
                    />
                    {isAgentLine && item.priceMatched && item.originalLineTotal != null ? (
                      <Box sx={{ textAlign: "right" }}>
                        <Typography variant="caption" color="text.disabled" sx={{ textDecoration: "line-through", display: "block" }}>
                          ${fmt(item.originalLineTotal)}
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="success.main">
                          ${fmt(lineTotal)}
                        </Typography>
                      </Box>
                    ) : lineTotal > 0 ? (
                      <Typography variant="body1" fontWeight="medium">${fmt(lineTotal)}</Typography>
                    ) : null}
                  </ListItem>
                  {index < items.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Item details are not available for this order.
          </Typography>
        )}

        {/* Price breakdown */}
        {total > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={0.5}>
              {priceRows.map(({ label, value, bold, green, free }) => (
                <React.Fragment key={label}>
                  <Grid item xs={6}>
                    <Typography variant="body2" fontWeight={bold ? "bold" : "normal"} color="text.secondary">{label}</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: "right" }}>
                    <Typography
                      variant="body2"
                      fontWeight={bold ? "bold" : "normal"}
                      color={green || free ? "success.main" : "text.primary"}
                    >
                      {value}
                    </Typography>
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          </>
        )}
      </Paper>

      {/* Help / actions */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: "background.default", borderRadius: 2 }}>
        <Typography variant="h6" align="center" gutterBottom>Need Help?</Typography>
        <Typography variant="body2" align="center" color="text.secondary" paragraph>
          Our support team is here to assist you.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
          <Button variant="outlined" onClick={() => (window.location.href = "mailto:support@orbiee.com")} sx={{ textTransform: "none" }}>
            Contact Support
          </Button>
          <Button variant="outlined" onClick={() => navigate("/order-history")} sx={{ textTransform: "none" }}>
            View Order History
          </Button>
          <Button variant="contained" onClick={() => navigate("/")} sx={{ textTransform: "none", bgcolor: "black", "&:hover": { bgcolor: "#111" } }}>
            Continue Shopping
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}
